import { Component } from "@angular/core";
import { GetRowIdParams, GridReadyEvent } from "@ag-grid-community/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Entity } from "breeze-client";
import _ from "lodash";
import { AgFns, GridOptionsExt } from "@/shared/ag-fns";
import { DialogBaseComponent, DialogParams } from "@/shared/dialog-base.component";
import { DomainService } from "@/shared/domain.service";
import { ActiveStatusEnum, ProductFeatureChoice } from "@/model/entities/entity-model";
import { ProductExposureApiService, ProductExposureFns } from "@/shared/product-exposure-api.service";
import { StagedFeedApiService } from "./staged-feed-api.service";
import { BulkRepointScopeDTO, BulkRepointStyleDTO, RepointCandidateColorDTO } from "./staged-feed.models";

export interface BulkRepointColorDialogParams extends DialogParams {
  manufacturerId: number;
  manufacturerName: string;
}

export interface BulkRepointColorResult {
  repointedProducts: number;
  combinedProducts: number;
  changedStyles: number;
  toName: string;
}

interface TargetChoice {
  id: number;
  label: string;
}

/**
 * Moves one Uniformax color to another across every style of a manufacturer at once.
 *
 * The per-style Change button does the same thing one style at a time, and refuses outright
 * when the style already carries the target color. This works per SIZE instead, because that
 * refusal is what made the job manual: 'Steel Grey' and 'Grey Steel' are both real SanMar
 * names, and a style can carry some sizes under one and some under the other.
 *
 * Two operations, decided per size by the server:
 *
 *   Re-point - the size exists only in the from-color. Done here through Breeze, because only
 *              a Breeze save writes the ProxSubmission rows that carry a color change out.
 *   Combine  - the size exists in both colors. Re-pointing it would leave two active products
 *              with an identical feature-choice set, which breaks supplier variant resolution
 *              on the Proximity side. CombineProducts folds them together instead, moving job
 *              order, purchase order and item detail rows onto the survivor and writing its own
 *              IsCombination submission.
 *
 * Combine deletes products and cannot be undone, so the scan reports both counts before Apply.
 *
 * Saves once per style rather than once overall. A failure then costs one style, not the run,
 * and the progress line means something while 500 products move.
 */
@Component({
  templateUrl: "./bulk-repoint-color-dialog.component.html",
  standalone: false,
})
export class BulkRepointColorDialogComponent extends DialogBaseComponent<BulkRepointColorDialogParams> {
  gridOptions: GridOptionsExt;

  candidates: RepointCandidateColorDTO[] = [];
  targets: TargetChoice[] = [];

  fromFeatureChoiceId: number | null = null;
  toFeatureChoiceId: number | null = null;

  scope: BulkRepointScopeDTO | null = null;
  isScanning = false;
  isApplying = false;
  errorMessage = "";

  progressDone = 0;
  progressTotal = 0;
  repointedProducts = 0;
  combinedProducts = 0;
  changedStyles = 0;
  failedStyles: string[] = [];

  constructor(
    public config: DynamicDialogConfig<BulkRepointColorDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private api: StagedFeedApiService,
    private productExposureApi: ProductExposureApiService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, { header: "Change a Color Across Every Style", width: "1050px", height: "860px" });

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (p: GetRowIdParams) => String((p.data as BulkRepointStyleDTO).ProductTypeId),
      cellSelection: false,
      getRowClass: (p: any) =>
        (BulkRepointColorDialogComponent.isActionable(p.data as BulkRepointStyleDTO) ? "" : "rs-already-processed"),
    });

    this.prepare();
  }

  private async prepare() {
    this.candidates = await this.api.getRepointCandidates(this.params.manufacturerId);

    const colorFeatureId = await this.dbQueryService.getColorFeatureId();
    const choices = colorFeatureId == null ? [] : await this.dbQueryService.getFeatureChoices(colorFeatureId);
    this.targets = _.sortBy(
      choices
        .filter(fc => fc.activeStatusId !== ActiveStatusEnum.Inactive)
        .map(fc => ({ id: fc.id, label: fc.choiceValue })),
      t => t.label.toLowerCase()
    );

    this.isPageReady = true;
  }

  static isActionable(row: BulkRepointStyleDTO) {
    return row?.Verdict === "Repoint" || row?.Verdict === "Merge";
  }

  /**
   * The colors worth changing lead the list: ones the manufacturer never publishes, by how
   * many products carry them. A color the feed does publish everywhere is already correct and
   * sits at the bottom, still selectable, because an operator may want to merge it anyway.
   */
  get candidateOptions() {
    return _.orderBy(
      this.candidates.map(c => ({
        ...c,
        label: `${c.ColorName}  —  ${c.ProductCount} product(s), ${c.StyleCount} style(s)`
          + (c.StylesWhereFeedPublishesIt > 0
            ? `, the feed publishes it on ${c.StylesWhereFeedPublishesIt}`
            : ", the feed never publishes it"),
      })),
      [c => (c.StylesWhereFeedPublishesIt === 0 ? 0 : 1), c => -c.ProductCount],
      ["asc", "asc"]
    );
  }

  get canScan() {
    return this.fromFeatureChoiceId != null
      && this.toFeatureChoiceId != null
      && this.fromFeatureChoiceId !== this.toFeatureChoiceId
      && !this.isScanning
      && !this.isApplying;
  }

  get actionableStyles(): BulkRepointStyleDTO[] {
    return (this.scope?.Styles ?? []).filter(s => BulkRepointColorDialogComponent.isActionable(s));
  }

  get repointCount() {
    return _.sumBy(this.actionableStyles, s => s.RepointProductIds.length);
  }

  get combineCount() {
    return _.sumBy(this.actionableStyles, s => s.CombinePairs.length);
  }

  get skippedStyles(): BulkRepointStyleDTO[] {
    return (this.scope?.Styles ?? []).filter(s => !BulkRepointColorDialogComponent.isActionable(s));
  }

  get skippedProductCount() {
    return _.sumBy(this.skippedStyles, s => s.FromProductCount);
  }

  get canApply() {
    return this.scope != null && this.actionableStyles.length > 0 && !this.isApplying && !this.isScanning;
  }

  async onScan() {
    if (!this.canScan) return;
    this.isScanning = true;
    this.errorMessage = "";
    this.scope = null;
    this.repointedProducts = 0;
    this.combinedProducts = 0;
    this.changedStyles = 0;
    this.failedStyles = [];
    try {
      this.scope = await this.api.getBulkRepointScope(
        this.params.manufacturerId, this.fromFeatureChoiceId, this.toFeatureChoiceId);
      this.gridOptions.apiX?.setGridOption("rowData", this.scope.Styles);
    } catch (e: any) {
      this.errorMessage = e?.error?.Message ?? e?.message ?? "The system could not read the styles.";
    }
    this.isScanning = false;
  }

  async onApply() {
    if (!this.canApply || this.scope == null) return;
    const scope = this.scope;
    const styles = this.actionableStyles;

    const combineWarning = this.combineCount > 0
      ? `<br/><br/><b>${this.combineCount} size(s) exist in both colors.</b> The system combines each`
        + ` pair into one product. The job orders, purchase orders and item details move to the`
        + ` product that stays. You cannot undo this.`
      : "";

    // A combined product's orders move to a product of the new color, so they change color too.
    this.isScanning = true;
    const changing = styles.flatMap(s => [...s.RepointProductIds, ...s.CombinePairs.map(p => p.FromProductId)]);
    const use = await this.productExposureApi.getForProducts(changing).catch(() => null);
    this.isScanning = false;

    const ok = await this.dialogService.yesNoDialog(
      "Change Color",
      `On ${styles.length} style(s), the system changes the color of ${this.repointCount} product(s)`
        + ` from '${scope.FromColorName}' to '${scope.ToColorName}'.`
        + combineWarning
        + ProductExposureFns.reassignColorWarning(use)
        + `<br/><br/>The change is sent to Proximity on the next push. Proximity price lists and`
        + ` program product lists that refer to this color are affected.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isApplying = true;
    this.errorMessage = "";
    this.progressTotal = styles.length;
    this.progressDone = 0;
    this.repointedProducts = 0;
    this.combinedProducts = 0;
    this.changedStyles = 0;
    this.failedStyles = [];

    for (const row of styles) {
      try {
        const done = await this.applyOneStyle(row, scope);
        if (done > 0) this.changedStyles++;
      } catch (e: any) {
        this.uow.rollback();
        this.failedStyles.push(row.Style);
        console.error(`Bulk repoint failed on style ${row.Style}`, e);
      }
      this.progressDone++;
    }

    this.isApplying = false;

    if (this.failedStyles.length > 0) {
      this.errorMessage = `The system could not change ${this.failedStyles.length} style(s):`
        + ` ${this.failedStyles.slice(0, 10).join(", ")}.`;
      return;
    }
    this.dialogRef.close({
      repointedProducts: this.repointedProducts,
      combinedProducts: this.combinedProducts,
      changedStyles: this.changedStyles,
      toName: scope.ToColorName,
    } as BulkRepointColorResult);
  }

  /**
   * One style. Combines run first so the re-point query that follows sees the style as it is
   * after the merge - otherwise the Breeze cache would still hold products the server deleted.
   * The two sets are disjoint, so neither step can undo the other.
   */
  private async applyOneStyle(row: BulkRepointStyleDTO, scope: BulkRepointScopeDTO): Promise<number> {
    let done = 0;

    // CombineProducts takes many losers and one survivor, so one call per survivor.
    const bySurvivor = _.groupBy(row.CombinePairs, p => p.ToProductId);
    for (const toProductId of Object.keys(bySurvivor)) {
      const fromProductIds = bySurvivor[toProductId].map(p => p.FromProductId);
      await this.dbSaveService.combineProducts(fromProductIds, Number(toProductId));
      this.combinedProducts += fromProductIds.length;
      done += fromProductIds.length;
    }

    if (row.RepointProductIds.length > 0) {
      const wanted = new Set(row.RepointProductIds);
      const products = await this.dbQueryService.getProductsForProductType(row.ProductTypeId);
      const touched: Entity[] = [];

      products
        .filter(p => wanted.has(p.id) && p.activeStatusId !== ActiveStatusEnum.Inactive)
        .forEach(p => {
          const old = p.productFeatureChoices.find(x => x.featureChoiceId === scope.FromFeatureChoiceId);
          if (old == null) return;
          // Re-check at save time, not just at scan time. The scan is a snapshot, and another
          // operator can put the target color on this product in between - which would double
          // the color up rather than move it.
          if (p.productFeatureChoices.some(x => x.featureChoiceId === scope.ToFeatureChoiceId)) return;

          // Delete then add. The pair is the primary key, so an update is not available.
          old.entityAspect.setDeleted();
          touched.push(old);
          touched.push(this.dbSaveService.createEntity(ProductFeatureChoice, {
            productId: p.id,
            featureChoiceId: scope.ToFeatureChoiceId,
          }));
        });

      if (touched.length > 0) {
        const saved = await this.dbSaveService.saveSelectedChanges(touched);
        if (!saved) {
          this.uow.rollback();
          throw new Error(`The save was refused on style ${row.Style}.`);
        }
        this.repointedProducts += touched.length / 2;
        done += touched.length / 2;
      }
    }

    return done;
  }

  onGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      { headerName: "Style", field: "Style", filter: "agTextColumnFilter", ...AgFns.flexWidth(140) },
      {
        headerName: "Result",
        field: "Verdict",
        filter: "agSetColumnFilter",
        ...AgFns.flexWidth(210),
        valueFormatter: (p: any) => BulkRepointColorDialogComponent.verdictLabel(p.value),
      },
      {
        headerName: "Sizes to Change",
        colId: "repointCount",
        type: "numericColumn",
        ...AgFns.fixWidth(150),
        valueGetter: (p: any) => (p.data as BulkRepointStyleDTO)?.RepointProductIds?.length ?? 0,
      },
      {
        headerName: "Sizes to Combine",
        colId: "combineCount",
        type: "numericColumn",
        ...AgFns.fixWidth(160),
        valueGetter: (p: any) => (p.data as BulkRepointStyleDTO)?.CombinePairs?.length ?? 0,
      },
      {
        headerName: "Feed Read",
        field: "SnapshotTs",
        ...AgFns.flexWidth(150),
        valueFormatter: (p: any) => (p.value ? new Date(p.value).toLocaleDateString() : ""),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "Style", sort: "asc" }]);
  };

  static verdictLabel(v: string): string {
    switch (v) {
      case "Repoint": return "Change the color";
      case "Merge": return "Change and combine";
      case "NoTargetInFeed": return "The feed has no such color";
      case "NotRead": return "The style is not read yet";
      default: return v ?? "";
    }
  }

  onCancel() {
    this.uow.rollback();
    this.dialogRef.close(null);
  }
}
